import { type KeyboardEvent, useId, useRef, useState } from "react";

/** The key code of a key press an IME handles. Safari sends it with the Enter that commits a composition, after clearing `isComposing`. */
const IME_KEY_CODE = 229;

export type ListNavigationKey = "ArrowDown" | "ArrowUp" | "Home" | "End";

export function isListNavigationKey(key: string): key is ListNavigationKey {
  return (
    key === "ArrowDown" || key === "ArrowUp" || key === "Home" || key === "End"
  );
}

/** The index a navigation key moves to, wrapping at both ends. -1 for an empty list. */
export function getNextIndex(
  current: number,
  key: ListNavigationKey,
  count: number,
): number {
  if (count <= 0) return -1;
  if (key === "Home") return 0;
  if (key === "End") return count - 1;
  if (current < 0 || current >= count) {
    return key === "ArrowDown" ? 0 : count - 1;
  }
  return key === "ArrowDown"
    ? (current + 1) % count
    : (current - 1 + count) % count;
}

/**
 * The active option: the one the user moved to, else a search's first match.
 * -1 when no option is active.
 */
export function getActiveIndex({
  movedIndex,
  hasSearch,
  optionCount,
}: {
  movedIndex: number | null;
  hasSearch: boolean;
  optionCount: number;
}): number {
  const index = movedIndex ?? (hasSearch ? 0 : -1);
  return index < 0 || optionCount <= 0 ? -1 : Math.min(index, optionCount - 1);
}

/**
 * Scrolls `list` so `option` is visible, or centered. It sets `scrollTop`
 * directly because `scrollIntoView` also scrolls the page behind a popover.
 * `list` must be the option's offset parent.
 */
export function scrollOptionIntoView(
  list: HTMLElement,
  option: HTMLElement,
  align: "nearest" | "center",
): void {
  const top = option.offsetTop;
  const bottom = top + option.offsetHeight;
  if (align === "center") {
    list.scrollTop = top - (list.clientHeight - option.offsetHeight) / 2;
  } else if (top < list.scrollTop) {
    list.scrollTop = top;
  } else if (bottom > list.scrollTop + list.clientHeight) {
    list.scrollTop = bottom - list.clientHeight;
  }
}

type UseMapperListboxOptions = {
  optionCount: number;
  /** While a search filters the list, its first match is active until the user moves it, so Enter picks it. */
  hasSearch: boolean;
  onChoose: (index: number) => void;
};

/**
 * Keyboard state for a search box that drives a listbox. No option is active
 * until the arrow keys move into the list or a search filters it. The arrow
 * keys move the active option and wrap, Enter chooses it, and focus stays in
 * the search box, so a long list is still a single tab stop. Home and End stay
 * with the search box's text cursor.
 */
export function useMapperListbox({
  optionCount,
  hasSearch,
  onChoose,
}: UseMapperListboxOptions) {
  const listId = useId();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [movedActiveIndex, setMovedActiveIndex] = useState<number | null>(null);
  const activeIndex = getActiveIndex({
    movedIndex: movedActiveIndex,
    hasSearch,
    optionCount,
  });
  const getOptionId = (index: number) => `${listId}-option-${index}`;

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    // While an IME composes, the arrows and Enter move through and commit its candidates.
    if (event.nativeEvent.isComposing || event.keyCode === IME_KEY_CODE) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const nextIndex = getNextIndex(activeIndex, event.key, optionCount);
      // An empty list has nowhere to move, and recording -1 would keep a later search match from becoming active.
      if (nextIndex < 0) return;
      setMovedActiveIndex(nextIndex);
      const option = document.getElementById(getOptionId(nextIndex));
      if (scrollRef.current && option) {
        scrollOptionIntoView(scrollRef.current, option, "nearest");
      }
    } else if (event.key === "Enter" && activeIndex >= 0) {
      event.preventDefault();
      onChoose(activeIndex);
    }
  };

  return {
    listId,
    scrollRef,
    /** False while the pane shows a loading or empty state instead of the listbox. */
    hasOptions: optionCount > 0,
    activeIndex,
    activeOptionId: activeIndex >= 0 ? getOptionId(activeIndex) : undefined,
    getOptionId,
    /**
     * Call whenever the options are replaced, as on a new search or tab, so a
     * remembered index doesn't carry over into them. It scrolls the list to
     * the top, where the first match is.
     */
    resetActiveIndex: () => {
      setMovedActiveIndex(null);
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
    },
    handleKeyDown,
  };
}
